import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, Star, ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { getProducts, DEFAULT_PRODUCTS } from "@/lib/content";
import type { Product } from "@/types/content";

const CATEGORIES = ["T-Shirts", "Hoodies", "Wallets", "Totes", "Others"];

const ProductCarouselSection = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>(DEFAULT_PRODUCTS);
  const [activeCategory, setActiveCategory] = useState("T-Shirts");
  const [currentIndex, setCurrentIndex] = useState(10000); 
  const [displayText, setDisplayText] = useState<Product | null>(DEFAULT_PRODUCTS[0]);
  const [textOpacity, setTextOpacity] = useState(1);
  const prevOffsets = useRef<Record<string, number>>({});

  useEffect(() => {
    const handleUpdate = (data: Product[]) => {
      setProducts(data);
      if (data.length > 0) {
        setDisplayText((prev) => {
          if (!prev || prev.name === DEFAULT_PRODUCTS[0].name) {
            return data[0];
          }
          const updated = data.find(p => p.name === prev.name);
          return updated || data[0];
        });
      }
    };

    getProducts(handleUpdate).then(handleUpdate);
  }, []);

  const len = products.length;
  const safeIndex = len > 0 ? currentIndex % len : 0;
  const activeProduct = len > 0 ? products[safeIndex] : null;

  useEffect(() => {
    if (!activeProduct) return;
    setTextOpacity(0);
    const timeout = setTimeout(() => {
      setDisplayText(activeProduct);
      setTextOpacity(1);
    }, 400); 
    return () => clearTimeout(timeout);
  }, [activeProduct]);

  const handlePrev = () => {
    if (len === 0) return;
    setCurrentIndex((prev) => prev - 1);
  };

  const handleNext = () => {
    if (len === 0) return;
    setCurrentIndex((prev) => prev + 1);
  };

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const minSwipeDistance = 50;

  const onTouchStart = (e: React.TouchEvent | React.MouseEvent) => {
    setTouchEnd(null);
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchStart(clientX);
  };

  const onTouchMove = (e: React.TouchEvent | React.MouseEvent) => {
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    setTouchEnd(clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNext();
    } else if (isRightSwipe) {
      handlePrev();
    }
  };

  return (
    <section className="py-20 md:py-28 bg-[#FAF8F5] relative overflow-hidden border-t border-border/40" id="products-list">
      <div className="container px-4 md:px-8">
        
        <div className="text-center mb-8 md:mb-12 space-y-2">
          <p className="text-muted-foreground font-body font-light tracking-[0.25em] uppercase text-[10px] md:text-xs">COLLECTION</p>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-display font-light text-foreground uppercase tracking-[0.3em]">OUR PRODUCTS</h2>
        </div>
 
        <div 
          className="text-center mb-8 md:mb-10 min-h-[100px] md:min-h-[120px] transition-all duration-500 ease-in-out"
          style={{ opacity: textOpacity, transform: `translateY(${textOpacity === 1 ? '0' : '15px'})` }}
        >
          <h3 className="text-lg sm:text-xl md:text-2xl font-display font-light text-foreground uppercase tracking-[0.2em] mb-4 px-4">
            {displayText?.name}
          </h3>
          <div className="flex flex-col items-center gap-3 md:gap-4">
            <div className="flex items-center justify-center gap-3 text-[10px] sm:text-xs font-body font-light">
              {displayText?.tag && (
                <span className="text-foreground/60 uppercase tracking-[0.2em]">
                  {displayText.tag}
                </span>
              )}
              <div className="flex items-center gap-1.5 md:gap-2 text-[#FFB800]">
                <div className="flex gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={11} fill="currentColor" strokeWidth={0} className="md:w-3 md:h-3" />
                  ))}
                </div>
                <span className="text-muted-foreground font-body font-light text-xs md:text-sm">5.0</span>
              </div>
            </div>
 
            <button
              onClick={() => {
                if (displayText) {
                  addToCart(displayText.name, displayText.price);
                  navigate('/checkout');
                }
              }}
              className="bg-[#171717] hover:bg-[#2A2A2A] text-white px-8 py-3.5 sm:px-12 sm:py-4 rounded-none font-body font-light text-[10px] sm:text-xs uppercase tracking-[0.25em] transition-colors shadow-none active:scale-98 flex items-center justify-center"
            >
              Order Now — ৳{displayText?.price}
            </button>
          </div>
        </div>
 
        <div 
          className="relative w-full max-w-[1600px] mx-auto flex items-center justify-center min-h-[350px] md:min-h-[580px] overflow-hidden touch-pan-y"
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onMouseDown={onTouchStart}
          onMouseMove={(e) => touchStart && onTouchMove(e)}
          onMouseUp={onTouchEnd}
          onMouseLeave={onTouchEnd}
        >
          
          <button 
            onClick={handlePrev}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 left-4 md:left-12 lg:left-16 z-40 w-12 h-12 items-center justify-center rounded-none bg-[#FAF8F5]/80 backdrop-blur-sm border border-border text-foreground hover:bg-[#EAE6DF] transition-colors"
          >
            <ChevronLeft size={20} strokeWidth={1.5} />
          </button>
 
          <div className="relative w-full h-[320px] md:h-[500px] flex items-center justify-center [perspective:1000px]">
            {products.map((product, index) => {
              let offset = (index - safeIndex) % len;
              if (offset < 0) offset += len; 
              if (offset > Math.floor(len / 2)) offset -= len; 
              
              const prevOffset = prevOffsets.current[product.id || index];
              const isJump = prevOffset !== undefined && Math.abs(offset - prevOffset) > (len / 2);
              
              prevOffsets.current[product.id || index] = offset;
 
              const isActive = offset === 0;
              const isPrev = offset === -1;
              const isNext = offset === 1;
              const isFarPrev = offset === -2;
              const isFarNext = offset === 2;
 
              let translateX = '0%';
              let translateY = '-50%';
              let translateZ = '0px';
              let scale = 1;
              let opacity = 1;
              let blur = '0px';
              let zIndex = 0;
 
              const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
              const desktopSpacing = 140;
              const mobileSpacing = 100;
              const spacing = isMobile ? mobileSpacing : desktopSpacing;
 
              if (isActive) {
                translateX = '0%';
                translateY = '-50%';
                translateZ = '0px';
                scale = 1;
                opacity = 1;
                blur = '0px';
                zIndex = 30;
              } else if (isPrev) {
                translateX = isMobile ? '0%' : `-${spacing}%`;
                translateY = isMobile ? '-50%' : '-10%';
                translateZ = isMobile ? '0px' : '-450px';
                scale = isMobile ? 0.8 : 0.68;
                opacity = isMobile ? 0 : 0.6;
                blur = isMobile ? '0px' : '4px';
                zIndex = 10;
              } else if (isNext) {
                translateX = isMobile ? '0%' : `${spacing}%`;
                translateY = isMobile ? '-50%' : '-10%';
                translateZ = isMobile ? '0px' : '-450px';
                scale = isMobile ? 0.8 : 0.68;
                opacity = isMobile ? 0 : 0.6;
                blur = isMobile ? '0px' : '4px';
                zIndex = 10;
              } else if (isFarPrev) {
                translateX = isMobile ? '0%' : `-${spacing * 1.8}%`;
                translateY = isMobile ? '-50%' : '-10%';
                translateZ = isMobile ? '0px' : '-600px';
                scale = isMobile ? 0.8 : 0.5;
                opacity = 0;
                blur = isMobile ? '0px' : '8px';
                zIndex = 0;
              } else if (isFarNext) {
                translateX = isMobile ? '0%' : `${spacing * 1.8}%`;
                translateY = isMobile ? '-50%' : '-10%';
                translateZ = isMobile ? '0px' : '-600px';
                scale = isMobile ? 0.8 : 0.5;
                opacity = 0;
                blur = isMobile ? '0px' : '8px';
                zIndex = 0;
              } else {
                translateX = '0%';
                translateY = '-50%';
                translateZ = '-800px';
                scale = 0.3;
                opacity = 0;
                blur = isMobile ? '0px' : '12px';
                zIndex = -10;
              }
 
              const transitionStyle = isJump 
                ? 'none' 
                : 'all 1000ms cubic-bezier(0.4, 0, 0.2, 1)';
 
              return (
                <div
                  key={product.id || index}
                  className={`absolute top-1/2 w-64 h-64 md:w-[480px] md:h-[480px] cursor-pointer select-none [transform-style:preserve-3d]`}
                  onClick={() => {
                    if (isPrev) handlePrev();
                    if (isNext) handleNext();
                  }}
                  style={{
                    transition: transitionStyle,
                    transform: `translateY(${translateY}) translateX(${translateX}) translateZ(${translateZ}) scale(${scale})`,
                    opacity: opacity,
                    filter: isMobile ? undefined : `blur(${blur})`,
                    zIndex: zIndex,
                    pointerEvents: (isActive || isPrev || isNext || isFarPrev || isFarNext) ? 'auto' : 'none',
                  }}
                >
                  <img 
                    src={product.image} 
                    alt={product.name} 
                    width="480"
                    height="480"
                    className="w-full h-full object-contain"
                    draggable="false"
                    loading={isActive ? "eager" : "lazy"}
                  />
                </div>
              );
            })}
          </div>
 
          <button 
            onClick={handleNext}
            className="hidden md:flex absolute top-1/2 -translate-y-1/2 right-4 md:right-12 lg:right-16 z-40 w-12 h-12 items-center justify-center rounded-none bg-[#FAF8F5]/80 backdrop-blur-sm border border-border text-foreground hover:bg-[#EAE6DF] transition-colors"
          >
            <ChevronRight size={20} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProductCarouselSection;
